<script setup lang="ts">
import { useModalStore } from "@/stores/modals";
import InfoCircle from "./InfoCircle.vue";

/**
 * ⓘ button that opens a markdown document in a modal.
 *
 * This used to be a third mode of `InfoIcon`, reached by passing `markdownPath` instead of `tooltip`.
 * It never showed a tooltip: it renders a button, opens a modal on click, and has nothing to do with
 * hover. Nine of that component's usages were this, so the two lived under one name while sharing only
 * the icon — which is now `InfoCircle`, used by both.
 *
 * Use it for documentation too long to sit in a tooltip. For a short hover hint, use `InfoIcon`; for a
 * hint on an element you already render, `v-tooltip`.
 */

const props = defineProps<{
  /** Document key resolved by ModalMarkdown, e.g. "emissionOverview". */
  markdownPath: string;
  inverted?: boolean;
}>();

const modals = useModalStore();

const open = () => {
  modals.open(
    "ModalMarkdown",
    { markdownPath: props.markdownPath },
    { wrapperClass: "max-w-[800px]! min-w-0 min-h-[0px]!", stackable: true }
  );
};
</script>

<template>
  <button
    type="button"
    aria-label="More information"
    class="flex p-1 transition-colors"
    @click="open">
    <InfoCircle :inverted="inverted" />
  </button>
</template>
